import type { IChatMessage } from '@kognitos/lattice';

import type { V1EventsDeleted } from '@/generated/hooks/rain/models';

/** Inclusive `[from, to]` span of server event ids a rollback removed. */
export interface IDeletedEventRange {
  from: number;
  to: number;
}

/**
 * Parses an `events_deleted` notification into a numeric range. Returns null
 * for a missing, non-numeric or inverted range, so a malformed notification
 * skips reconciliation instead of clearing an unbounded span of transcript.
 */
export const parseDeletedEventRange = (
  deleted: V1EventsDeleted | undefined,
): IDeletedEventRange | null => {
  // Falsy (not just undefined): an empty string is what a gateway emitting
  // proto3 defaults sends for an unset field, and `Number('')` is 0 — which
  // would delete from the top of the thread.
  if (!deleted?.from_event_id || !deleted.to_event_id) return null;
  const from = Number(deleted.from_event_id);
  const to = Number(deleted.to_event_id);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  if (to < from) return null;
  return { from, to };
};

/** The span of server event ids a single rendered message was built from. */
export interface IMessageOriginSpan {
  first: number;
  last: number;
}

/**
 * Reverse index from a rendered message id to its origin span. One message can
 * have many origins — a tool card accumulates start/argument/result events, an
 * assistant bubble one per streamed delta — but only the outer bounds matter
 * for an overlap test, so the span stays O(1) per message rather than growing
 * with the token count.
 */
export type TMessageOriginIndex = Map<string, IMessageOriginSpan>;

/** Ignores id-less events: they never entered the log, so no range covers them. */
export const recordMessageOrigin = (
  index: TMessageOriginIndex,
  messageId: string,
  eventId: string | undefined,
): void => {
  if (eventId === undefined) return;
  const id = Number(eventId);
  if (!Number.isFinite(id)) return;
  const existing = index.get(messageId);
  if (existing) {
    if (id < existing.first) existing.first = id;
    if (id > existing.last) existing.last = id;
    return;
  }
  index.set(messageId, { first: id, last: id });
};

/**
 * Collects messages whose origin span overlaps the rolled-back range, and
 * prunes them from the index. Overlap rather than containment: rain rolls back
 * whole turns, so a message straddling the boundary belongs to a turn that no
 * longer exists and must go rather than render torn.
 */
export const collectRolledBackMessageIds = (
  index: TMessageOriginIndex,
  range: IDeletedEventRange,
): Set<string> => {
  const rolledBack = new Set<string>();
  for (const [messageId, span] of index) {
    if (span.first <= range.to && span.last >= range.from) {
      rolledBack.add(messageId);
    }
  }
  for (const messageId of rolledBack) index.delete(messageId);
  return rolledBack;
};

/** Returns the same reference when nothing is dropped, so React can bail out. */
export const dropMessagesById = (
  messages: IChatMessage[],
  removedIds: ReadonlySet<string>,
): IChatMessage[] => {
  if (removedIds.size === 0) return messages;
  const next = messages.filter(m => !removedIds.has(m.id));
  return next.length === messages.length ? messages : next;
};

/**
 * Drops derived-state entries backing rolled-back messages, so a retry
 * rebuilds them instead of merging into stale ones. The maps are keyed by
 * domain id (tool_call_id, interrupt_id, …) while `removedIds` holds message
 * ids, so the caller supplies the builders' key→message-id derivation.
 *
 * Returns the deleted domain ids: pause-bearing state (interrupts, approvals,
 * sleeps, compactions) is mirrored in the generation reducer, which has to
 * drop the same ids or the pause never clears.
 */
export const pruneStateByMessageId = <T>(
  state: Map<string, T>,
  removedIds: ReadonlySet<string>,
  toMessageId: (key: string) => string,
): string[] => {
  if (removedIds.size === 0) return [];
  const deleted: string[] = [];
  for (const key of [...state.keys()]) {
    if (removedIds.has(toMessageId(key))) {
      state.delete(key);
      deleted.push(key);
    }
  }
  return deleted;
};
